import { LeaderboardPlayerData } from "../../../models/leaderboard";
import { MatchSetupConfig } from "../../../models/match";
import { formatDateToDDMMMYYYY } from "../../../utils/dateUtils";

interface PlayersListSeasonLevelProps {
  players: LeaderboardPlayerData[];
  matchSetups: MatchSetupConfig[];
  teamName?: string;
}

const PlayerCard = ({ 
  player, 
  matchSetups, 
  index 
}: { 
  player: LeaderboardPlayerData; 
  matchSetups: MatchSetupConfig[];
  index: number;
}) => {

  return (
    <div 
      className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm mb-3 cursor-pointer hover:shadow-md transition-shadow"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center">
          {player.profilePicture && (
            <img
              src={`/api/static/images/${player.profilePicture}`}
              alt={`${player.firstName} ${player.lastName}`}
              className="w-10 h-10 rounded-full mr-3"
            />
          )}
          <div>
            <div className="font-semibold text-gray-900 dark:text-gray-100">
              {player.firstName} {player.lastName}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Rank: {index + 1}
            </div>
          </div>
        </div>
        <div className="text-xl font-bold text-gray-900 dark:text-gray-100">
          {player.totalStableford}
        </div>
      </div>

      <div className="space-y-2">
        {matchSetups.map((match) => (
          <div key={match.matchId} className="flex justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">
              {match.courseName} - {formatDateToDDMMMYYYY(match.date ?? '')}
            </span>
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {(player as any).matchScores?.[match.matchId] ?? '-'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const PlayerRow = ({ 
  player, 
  matchSetups, 
  index 
}: { 
  player: LeaderboardPlayerData; 
  matchSetups: MatchSetupConfig[];
  index: number;
}) => {

  return (
    <tr
      className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
    >
      <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100">
        {index + 1}
      </td>
      <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100">
        <div className="flex items-center">
          {player.profilePicture && (
            <img
              src={`/api/static/images/${player.profilePicture}`}
              alt={`${player.firstName} ${player.lastName}`}
              className="w-8 h-8 rounded-full mr-2"
            />
          )}
          <span>
            {player.firstName} {player.lastName}
          </span>
        </div>
      </td>
      {matchSetups.map((match) => (
        <td
          key={match.matchId}
          className="px-4 py-2 whitespace-nowrap text-center text-gray-900 dark:text-gray-100"
        >
          {(player as any).matchScores?.[match.matchId] ?? '-'}
        </td>
      ))}
      <td className="px-4 py-2 whitespace-nowrap text-center font-bold text-gray-900 dark:text-gray-100">
        {player.totalStableford}
      </td>
    </tr>
  );
};

export const PlayersListSeasonLevel = ({ 
  players, 
  matchSetups,
  teamName 
}: PlayersListSeasonLevelProps) => {
  const sortedPlayers = [...players].sort((a, b) => 
    (b.totalStableford || 0) - (a.totalStableford || 0)
  );

  if (!players.length) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        No players found {teamName ? `in ${teamName}` : ''}
      </div>
    );
  }

  return (
    <>
      {/* Desktop View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead>
            <tr>
              <th className="px-4 py-2 text-gray-900 dark:text-gray-100">Rank</th>
              <th className="px-4 py-2 text-gray-900 dark:text-gray-100">Player</th>
              {matchSetups.map((match) => (
                <th
                  key={match.matchId}
                  className="px-4 py-2 text-center text-gray-900 dark:text-gray-100"
                >
                  {match.courseName}<br/>
                  {formatDateToDDMMMYYYY(match.date ?? '')}
                </th>
              ))}
              <th className="px-4 py-2 text-center text-gray-900 dark:text-gray-100">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedPlayers.map((player, index) => (
              <PlayerRow
                key={player.id}
                player={player}
                matchSetups={matchSetups}
                index={index}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile View */}
      <div className="md:hidden">
        <div className="p-4">
          {sortedPlayers.map((player, index) => (
            <PlayerCard
              key={player.id}
              player={player}
              matchSetups={matchSetups}
              index={index}
            />
          ))}
        </div>
      </div>
    </>
  );
};
